import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import apiClient from '../../services/apiClient';
import TopBarTimezonePicker from './TopBarTimezonePicker';



const Layout = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [showProfileDropdown, setShowProfileDropdown] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const currentRole = (localStorage.getItem('role') || '').toLowerCase();
    const isAdmin = currentRole === 'admin';

    const [availableBalance, setAvailableBalance] = useState<number | null>(null);

    // Fetch balance only for subadmin/agent/operator roles
    useEffect(() => {
        if (isAdmin) return;
        const fetchBalance = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) return;
                const res = await apiClient.get(`/admin/getMineProfile`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const profile = res?.data?.data;
                if (profile && profile.available_credit !== undefined) {
                    setAvailableBalance(parseFloat(profile.available_credit) || 0);
                }
            } catch (e) {
                console.error('Failed to fetch balance for header:', e);
            }
        };
        fetchBalance();
        // Refresh every 30s so balance stays current after distributing to users
        const interval = setInterval(fetchBalance, 30000);
        return () => clearInterval(interval);
    }, [isAdmin]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowProfileDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLogout = () => {
        const role = (localStorage.getItem("role") || "").toLowerCase();
        localStorage.clear();
        if (role === "affiliate") {
            navigate("/login/affiliate");
        } else if (role === "subadmin" || role === "manager") {
            navigate("/login/manager");
        } else {
            navigate("/login");
        }
    };

    const getTopBarTitle = (pathname: string) => {
        switch (pathname) {
            case '/': return 'Dashboard';
            case '/users': return 'Users';
            case '/deposit': return 'Deposits';
            case '/house': return 'House Edge';
            case '/withdraw': return 'Withdraw';
            case '/wallet': return 'User Wallet';
            case '/history': return 'History';
            default: return 'App';
        }
    };

    return (
        <div className='flex h-screen bg-[#020617] overflow-hidden'>
            <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
            <div className='flex-1 flex flex-col overflow-hidden'>

                {/* ── Top Header Bar (Stake Style) ── */}
                <header className="h-14 bg-slate-900 border-b border-slate-800 flex items-center justify-between px-4 shrink-0 z-40 sticky top-0 shadow-sm">
                    <div className="flex items-center gap-3">
                        <button 
                            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                            className="p-1.5 lg:hidden rounded-md hover:bg-slate-800 text-slate-400 transition-colors"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
                        </button>

                        <div className="hidden sm:flex flex-col min-w-0">
                            <h1 className="text-sm font-bold tracking-tight text-white uppercase leading-none truncate max-w-[120px] sm:max-w-none">{getTopBarTitle(location.pathname)}</h1>
                            <p className="hidden sm:block text-[8px] text-slate-400 font-bold uppercase tracking-widest leading-none mt-1 opacity-70 truncate">Admin Panel</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 sm:gap-4">
                        {/* Timezone Switcher */}
                        <TopBarTimezonePicker />

                        {/* Profile Trigger Container */}
                        <div className="relative" ref={dropdownRef}>
                            <div 
                                onClick={() => setShowProfileDropdown(!showProfileDropdown)} 
                                className="group flex items-center gap-2 pl-1 cursor-pointer select-none"
                            >
                                <div className="hidden sm:flex flex-col items-end">
                                    <span className="text-[11px] font-bold text-slate-200 leading-none group-hover:text-white transition-colors capitalize">{currentRole || 'Admin'} Staff</span>
                                    <span className="text-[9px] text-green-500 font-medium tracking-tight mt-0.5 opacity-80 italic">online</span>
                                </div>
                                <div className="w-7 h-7 rounded bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-400 group-hover:bg-slate-700 group-hover:text-white transition-all shadow-sm">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                                </div>
                            </div>

                            {/* Dropdown Menu */}
                            {showProfileDropdown && (
                                <div className="absolute right-0 top-10 w-44 bg-slate-900 border border-slate-800 rounded-lg shadow-xl py-1 z-50 animate-in fade-in slide-in-from-top-1 duration-150">
                                    <button
                                        onClick={() => {
                                            setShowProfileDropdown(false);
                                            navigate('/changePassword');
                                        }}
                                        className="w-full text-left px-3.5 py-2 text-xs font-semibold text-slate-300 hover:text-white hover:bg-slate-800 transition-colors flex items-center gap-2"
                                    >
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                                        Profile Settings
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowProfileDropdown(false);
                                            handleLogout();
                                        }}
                                        className="w-full text-left px-3.5 py-2 text-xs font-semibold text-red-400 hover:text-red-300 hover:bg-red-950/20 transition-colors flex items-center gap-2 border-t border-slate-800/60"
                                    >
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                                        Log out
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                <div className='flex-1 overflow-auto bg-[#020617] p-4 md:p-6'>
                    <Outlet />
                </div>
            </div>
        </div>
    );
};

export default Layout;